import { useTranslation } from "react-i18next";
import { FileText, Receipt, TrendingUp, Wallet } from "lucide-react";
import { Link } from "react-router-dom";

import type { DashboardFinancial, DashboardMetrics } from "@fiscalo/shared-types";

function formatAmount(amount: number): string {
  return amount.toLocaleString("es-UY", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

interface DashboardFinancialSummaryProps {
  metrics: DashboardMetrics;
  financial: DashboardFinancial;
}

interface StatCellProps {
  icon: React.ComponentType<{ className?: string }>;
  iconKind?: "ink" | "olive";
  label: string;
  currency?: string;
  value: string;
  plain?: boolean;
  accent?: boolean;
  trend?: string;
  /** When set, the whole card becomes a link to this route. */
  to?: string;
}

function StatCell({
  icon: Icon,
  iconKind = "ink",
  label,
  currency,
  value,
  plain,
  accent,
  trend,
  to,
}: StatCellProps): JSX.Element {
  const inner = (
    <>
      <div className="row">
        <div className={`ico-tile ${iconKind}`}>
          <Icon className="h-4 w-4" />
        </div>
        {trend && <span className="trend">{trend}</span>}
      </div>
      <div className="label">{label}</div>
      <div className={`num${plain ? " plain" : ""}`}>
        {currency && <span className="currency">{currency}</span>}
        <span className={`val${accent ? " accent" : ""}`}>{value}</span>
      </div>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className="fx-stat text-inherit no-underline transition-colors hover:bg-foreground/[0.025]"
      >
        {inner}
      </Link>
    );
  }
  return <div className="fx-stat">{inner}</div>;
}

export function DashboardFinancialSummary({
  metrics,
  financial,
}: DashboardFinancialSummaryProps): JSX.Element {
  const { t } = useTranslation();

  const hasUyu = financial.totalInvoicedThisMonthUyu > 0;
  const hasUsd = financial.totalInvoicedThisMonthUsd > 0;
  const totalInvoices = metrics.invoicesThisMonth;
  const ivaAmount = financial.ivaAPagarEstimadoUyu;
  const hasIva = typeof ivaAmount === "number" && ivaAmount !== 0;
  const ivaIsCredit = hasIva && ivaAmount < 0;

  const hasAnyMoney = hasUyu || hasUsd || hasIva;

  if (!hasAnyMoney && totalInvoices === 0) {
    return (
      <div className="fx-stats">
        <div className="fx-stat" style={{ gridColumn: "1 / -1", borderRight: 0 }}>
          <div className="label">
            {t("dashboard.financial.noActivity", "Sin actividad este mes.")}{" "}
            <Link
              to="/reports"
              className="font-semibold underline-offset-4 hover:underline"
              style={{ color: "var(--fx-accent-700)" }}
            >
              {t("dashboard.financial.viewReports", "Ver reportes")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fx-stats">
      <StatCell
        icon={FileText}
        iconKind="ink"
        label={t("dashboard.financial.invoicesThisMonth", "Facturas este mes")}
        value={String(totalInvoices)}
        plain
        to="/invoices?period=month"
      />
      {hasUyu && (
        <StatCell
          icon={TrendingUp}
          iconKind="ink"
          label={t("dashboard.financial.revenueUyu", "Facturado UYU")}
          currency="UYU"
          value={formatAmount(financial.totalInvoicedThisMonthUyu)}
          to="/invoices?period=month&currency=UYU"
        />
      )}
      {hasUsd && (
        <StatCell
          icon={TrendingUp}
          iconKind="ink"
          label={t("dashboard.financial.revenueUsd", "Facturado USD")}
          currency="USD"
          value={formatAmount(financial.totalInvoicedThisMonthUsd)}
          to="/invoices?period=month&currency=USD"
        />
      )}
      {hasIva && (
        <StatCell
          icon={ivaIsCredit ? Wallet : Receipt}
          iconKind="olive"
          label={
            ivaIsCredit
              ? t("dashboard.financial.ivaCredit", "IVA a favor")
              : t("dashboard.financial.ivaToPay", "IVA estimado a pagar")
          }
          currency="UYU"
          value={formatAmount(Math.abs(ivaAmount))}
          accent={ivaIsCredit}
          trend={ivaIsCredit ? t("dashboard.financial.credit", "crédito") : undefined}
          to="/estado-fiscal"
        />
      )}
    </div>
  );
}
